import React, {useState} from 'react';
import {Card, Dialog, DialogContent, DialogTitle, Grid, Tab, Tabs, Typography} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import {Stack} from "@mui/system";

// Hardcoded color values with light and dark shades
const PRIMARY_BLUE = '#1976d2'; // Used for Overview/Roles when selected
const GREY_DEFAULT = '#555'; // Used for Overview/Roles when unselected

// Green shades for 'Add' tab
const SUCCESS_GREEN_LIGHT = '#e8f2ed'; // Lighter green
const SUCCESS_GREEN_DARK = '#abd154';  // Darker green

// Orange shades for 'Modify' tab
const WARNING_ORANGE_LIGHT = '#fef4ec'; // Lighter orange
const WARNING_ORANGE_DARK = '#f09637';  // Darker orange

// Red shades for 'Remove' tab
const ERROR_RED_LIGHT = '#ffdcda'; // Lighter red
const ERROR_RED_DARK = '#FF1300';  // Darker red


// Reusable Step Number Circle Component
const StepNumberCircle = ({ step, color }) => (
    <Box
        sx={{
            position: 'absolute',
            top: '-15px', // Half of the circle's height to center it on the top edge
            left: '50%',
            transform: 'translateX(-50%)',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: color, // Changed this line to use 'color' for background
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
            zIndex: 1, // Ensure it's above the card
            border: `2px solid ${color}`, // Border matching the icon color
        }}
    >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}> {/* Changed text color to white for contrast */}
            {step}
        </Typography>
    </Box>
);

function InfoAttendees({ isOpen, onClose }){

    const [tabIndex, setTabIndex] = useState(0);
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };


    // Common Card Styling
    const commonCardSx = {
        borderRadius: '1rem',
        padding: '1.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'transform 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        '&:hover': {
            transform: 'translateY(-5px)',
        },
    };

    // Common Icon Container Styling
    const commonIconContainerSx = {
        padding: '0.75rem',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    // Responsive Box item styling
    const responsiveBoxItemSx = {
        flexGrow: 1, // Allows items to grow
        flexShrink: 1, // Allows items to shrink
        minWidth: '230px', // Minimum width before wrapping
        maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', lg: 'calc(33.33% - 16px)' }, // Responsive max width
        // For lg:col-span-3 (full width on lg screens)
        '&.full-width-lg': {
            maxWidth: { lg: '100%' },
        },
        // For lg:col-span-8 (2/3 width on lg screens)
        '&.two-thirds-width-lg': {
            maxWidth: { lg: 'calc(66.66% - 16px)' },
        },
    };

    // Styling for the Card component itself when it's a step card
    const stepCardBaseSx = {
        ...commonCardSx, // Inherit base card styles
        borderTop: '2px solid', // Add a top border
        paddingTop: '2.5rem', // Increase padding-top to make space for the circle content
        // Remove individual card hover transform here, as it will be on the parent Box
        '&:hover': {
            boxShadow: '0 8px 15px rgba(0, 0, 0, 0.1)', // Keep shadow change
        },
    };

    // Styling for the Box that wraps both the StepNumberCircle and the Card for numbered steps
    const responsiveBoxItemWithStepSx = {
        ...responsiveBoxItemSx, // Inherit base responsive styles
        position: 'relative', // Crucial for positioning the step number circle
        marginTop: '20px', // Push the entire box down to allow space for the circle above the card's border
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out', // Add transition to the parent box
        '&:hover': {
            transform: 'translateY(-5px)', // Apply hover transform to the parent box
        },
    };


    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle
                sx={{
                    color: '#151269',
                    textAlign: 'center',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    paddingRight: '30px'
                }}
            >
                Managing Your Guest List 👥
            </DialogTitle>

            <Typography
                sx={{
                    fontSize: '14px',
                    textAlign: 'center',
                    color: '#151269',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    padding: '0 20px'
                }}
            >
                **Add Attendees Like a Pro:** Unleash your inner social butterfly!
                Grab contacts straight from your phone (Android, high-five! 👋),
                snag familiar faces from past adventures, or type in those email
                addresses manually. Getting the guest list together has never been
                smoother!
            </Typography>

            {/* Close Button */}
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#151269',
                }}
            >
                <i className="icon-cross" style={{ fontSize: '18px', padding: '5px' }} />
            </IconButton>

            {/* Sticky Tabs */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    zIndex: 1,
                }}
            >
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    variant="scrollable"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    sx={{
                        '& .MuiTab-root': {
                            fontWeight: 'bold',
                            minHeight: 48,
                            textTransform: 'none',
                            transition: 'color 0.3s ease', // Smooth transition for color change
                            // Default minWidth for scrollable tabs (allows content to dictate width on mobile)
                            minWidth: 'auto',
                            padding: '6px 12px', // Default padding for tabs

                            // On medium and larger screens, make tabs grow to fill space
                            '@media (min-width:900px)': { // md breakpoint
                                minWidth: 0, // Allow tabs to shrink as needed
                                flexGrow: 1, // Allow tabs to grow and share space
                                maxWidth: 'unset', // Remove any max-width constraints on individual tabs
                            },
                        },
                        // Target the flex container inside Tabs that holds the Tab components
                        '& .MuiTabs-flexContainer': {
                            // On desktop, ensure flex context and distribute space
                            '@media (min-width:900px)': {
                                display: 'flex', // Ensure flex layout
                                flexGrow: 1, // Allow the container to grow
                                justifyContent: 'space-around', // Distribute space evenly among tabs
                            },
                        },
                    }}
                >
                    <Tab icon={<i className="icon-eye" style={{ fontSize: '18px' }} />} label="Overview" value={0} />
                    <Tab icon={<i className="icon-user-gear" style={{ fontSize: '18px' }} />} label="Roles" value={1} />
                    <Tab icon={<i className="icon-pencil" style={{ fontSize: '18px' }} />} label="Add" value={2} />
                    <Tab icon={<i className="icon-file-pencil" style={{ fontSize: '18px' }} />} label="Modify" value={3} />
                    <Tab icon={<i className="icon-trash" style={{ fontSize: '18px' }} />} label="Remove" value={4} />
                </Tabs>
            </Box>

            <DialogContent>

                {tabIndex === 0 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
                            Your Contacts & Event Attendees: A Clear Guide!
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                            Ever wonder how your master contact list connects with your event guest lists? Let's unravel the magic!
                        </Typography>

                        {/* SVG Diagram Section */}
                        <Box sx={{ my: 4, width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <svg viewBox="0 0 700 350" style={{ maxWidth: '100%', height: 'auto' }}>
                                <defs>
                                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                                        <polygon points="0 0, 10 3.5, 0 7" fill="#607d8b" />
                                    </marker>
                                </defs>

                                {/* Master Contact List Card */}
                                <rect x="20" y="50" width="200" height="250" rx="12" ry="12" fill="#E3F2FD" stroke="#2196F3" strokeWidth="2" />
                                <text x="120" y="80" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1976D2">Master Contacts</text>
                                <g transform="translate(100, 150)">
                                    <circle cx="0" cy="-20" r="25" fill="#BBDEFB" stroke="#2196F3" strokeWidth="1.5" />
                                    <path d="M-20,10 H20 V30 H-20 Z" fill="#BBDEFB" stroke="#2196F3" strokeWidth="1.5" />
                                    <text x="0" y="50" textAnchor="middle" fontSize="12" fill="#424242">Default Profile</text>
                                </g>
                                <g transform="translate(100, 240)">
                                    <circle cx="0" cy="-20" r="25" fill="#BBDEFB" stroke="#2196F3" strokeWidth="1.5" />
                                    <path d="M-20,10 H20 V30 H-20 Z" fill="#BBDEFB" stroke="#2196F3" strokeWidth="1.5" />
                                    <text x="0" y="50" textAnchor="middle" fontSize="12" fill="#424242">Default Profile</text>
                                </g>


                                {/* Event 1 Card */}
                                <rect x="300" y="20" width="180" height="150" rx="10" ry="10" fill="#E8F5E9" stroke="#4CAF50" strokeWidth="2" />
                                <text x="390" y="45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#388E3C">Event: Gala Night</text>
                                <g transform="translate(390, 100)">
                                    <circle cx="0" cy="-20" r="20" fill="#C8E6C9" stroke="#4CAF50" strokeWidth="1.5" />
                                    <path d="M-15,5 H15 V20 H-15 Z" fill="#C8E6C9" stroke="#4CAF50" strokeWidth="1.5" />
                                    <text x="0" y="35" textAnchor="middle" fontSize="11" fill="#424242">Event Profile</text>
                                    <text x="0" y="48" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#388E3C">"Star Guest"</text>
                                </g>

                                {/* Event 2 Card */}
                                <rect x="300" y="180" width="180" height="150" rx="10" ry="10" fill="#FFF3E0" stroke="#FF9800" strokeWidth="2" />
                                <text x="390" y="205" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#F57C00">Event: Workshop</text>
                                <g transform="translate(390, 260)">
                                    <circle cx="0" cy="-20" r="20" fill="#FFE0B2" stroke="#FF9800" strokeWidth="1.5" />
                                    <path d="M-15,5 H15 V20 H-15 Z" fill="#FFE0B2" stroke="#FF9800" strokeWidth="1.5" />
                                    <text x="0" y="35" textAnchor="middle" fontSize="11" fill="#424242">Event Profile</text>
                                    <text x="0" y="48" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#F57C00">"The Learner"</text>
                                </g>

                                {/* Event 3 Card */}
                                <rect x="500" y="100" width="180" height="150" rx="10" ry="10" fill="#FFEBEE" stroke="#F44336" strokeWidth="2" />
                                <text x="590" y="125" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#D32F2F">Event: Festival</text>
                                <g transform="translate(590, 180)">
                                    <circle cx="0" cy="-20" r="20" fill="#FFCDD2" stroke="#F44336" strokeWidth="1.5" />
                                    <path d="M-15,5 H15 V20 H-15 Z" fill="#FFCDD2" stroke="#F44336" strokeWidth="1.5" />
                                    <text x="0" y="35" textAnchor="middle" fontSize="11" fill="#424242">Event Profile</text>
                                    <text x="0" y="48" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#D32F2F">"Party Animal"</text>
                                </g>


                                {/* Arrows from Master to Events */}
                                <line x1="220" y1="160" x2="300" y2="100" stroke="#607d8b" strokeWidth="2" markerEnd="url(#arrowhead)" />
                                <line x1="220" y1="250" x2="300" y2="260" stroke="#607d8b" strokeWidth="2" markerEnd="url(#arrowhead)" />
                                <line x1="220" y1="200" x2="500" y2="180" stroke="#607d8b" strokeWidth="2" markerEnd="url(#arrowhead)" />

                                {/* Text for arrows */}
                                <text x="260" y="120" textAnchor="middle" fontSize="12" fill="#607d8b">Pulled to Event</text>
                                <text x="260" y="220" textAnchor="middle" fontSize="12" fill="#607d8b">Pulled to Event</text>
                                <text x="360" y="160" textAnchor="middle" fontSize="12" fill="#607d8b">Pulled to Event</text>

                            </svg>
                        </Box>

                        {/* Explanatory Text */}
                        <Box container spacing={4} sx={{ mt: 4, textAlign: 'left' }}>
                            <Box item xs={12} md={6}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <i className="icon-user-info" style={{ fontSize: 30 }} />
                                    <Typography variant="h6" component="h2" color="primary">
                                        The Master Contact List
                                    </Typography>
                                </Stack>
                                <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                    Think of this as your central, super-powered address book! It holds the default, core information for all your contacts. When you add a new person here, their details become the standard for any future events they might join.
                                </Typography>
                            </Box>
                            <Box item xs={12} md={6}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1}}>
                                    <i className="icon-group" style={{fontSize: 30}}/>
                                    <Typography variant="h6" component="h2" color="success">
                                        Event Attendees & Profiles
                                    </Typography>
                                </Stack>
                                <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                    When you add a contact to a specific event, their profile is "pulled" from your Master List. Now, here's the cool part: if you change something like their nickname or specific details *within that event*, those changes are saved permanently for *that event's record* of the attendee. It's like giving them a special event badge!
                                </Typography>
                            </Box>
                        </Box>

                        {/* Pro Tip Section */}
                        <Box sx={{ ...responsiveBoxItemSx, ...responsiveBoxItemSx['&.full-width-lg'] }}>
                            <Card sx={{
                                backgroundColor: '#dcfce7', // Light green
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                marginTop: '1.5rem', // Add some spacing
                                border: '2px solid #22c55e', // Green border
                            }}>
                                <i className="icon-lamp-bright" style={{ color: '#166534', fontSize: '2rem' }}/>
                                <Typography variant="h5" component="h4" sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, // font-bold
                                    color: '#166534', // Green-800
                                    marginBottom: '0.5rem',
                                }}>
                                    ✨ Pro Tip: ✨
                                </Typography>
                                <Typography variant="body1" sx={{
                                    fontSize: '14px',
                                    color: '#14532d', // Green-900
                                }}>
                                    Want to make a change that sticks for *all future events*? Head back to your **Master Contact List** to update
                                    their default profile. Event-specific changes are just for that event's unique vibe!
                                </Typography>
                            </Card>
                        </Box>

                    </Box>
                )}

                {tabIndex === 1 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        <Typography variant="h4" component="h2" sx={{
                            fontSize: '18px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Unlock Attendee Superpowers with Roles:
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '24px', // Equivalent to spacing={3}
                                marginBottom: '3rem',
                                justifyContent: 'center' // Center items when they wrap
                            }}
                        >
                            {/* Planner */}
                            <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                                <Card sx={{ ...commonCardSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-compass" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontSize: '18px',
                                            fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                        }}
                                    >
                                        Planner
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        This mastermind has all the planning power but prefers to stay behind the scenes during the actual fun.
                                        Think of them as your event's secret agent!
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Host */}
                            <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                                <Card sx={{ ...commonCardSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-crown" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontSize: '18px',
                                            fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                        }}
                                    >
                                        Host
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Need backup? Promote your MVPs to Host status! They'll have extra organizing
                                        power to help make everything run like clockwork.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Attendee */}
                            <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                                <Card sx={{ ...commonCardSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-user" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontSize: '18px',
                                            fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                        }}
                                    >
                                        Attendee
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Your everyday hero! They're in for the full experience, ready to dive into
                                        every activity and soak up all the good vibes.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Partial Attendee */}
                            <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                                <Card sx={{ ...commonCardSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-user-info" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontSize: '18px',
                                            fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                        }}
                                    >
                                        Partial Attendee
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Perfect for those cool cats who are only joining for a specific
                                        highlight – maybe just the legendary karaoke session or that mouthwatering brunch.
                                        They're here for a good time, not a long time!
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>

                        <Typography variant="h5" component="h5" sx={{
                            fontSize: '16px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Changing Attendee Role
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Select Attendee */}
                            <Box sx={responsiveBoxItemWithStepSx} >
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-search" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Select Attendee
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Locate the attendee whose role you wish to modify in the list.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Select Role */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-gear" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Access Role Options
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Click the three dots next to their name.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Confirm */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-check" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Confirm New Role
                                    </Typography>
                                    <Typography variant="body2" sx={{fontSize: '14px', color: '#4b5563'}}>
                                        Choose the desired role from the options and select.
                                        <br/><br/>
                                        <Typography component="span" variant="body2"
                                                    sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Pro Tip: </Typography>
                                        If they have an email they will be notified automatically
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>
                    </Box>
                )}

                {tabIndex === 2 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        <Typography variant="h5" component="h5" sx={{
                            fontSize: '16px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Adding New Attendees - It's best to have already given the Event a name and it's own cover image
                            for the RSVP responses
                        </Typography>

                        {/* Pro Tip Section */}
                        <Box sx={{ ...responsiveBoxItemSx, ...responsiveBoxItemSx['&.full-width-lg'] }}>
                            <Card sx={{
                                backgroundColor: '#dcfce7', // Light green
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                marginTop: '1.5rem', // Add some spacing
                                border: '2px solid #22c55e', // Green border
                            }}>
                                <i className="icon-lamp-bright" style={{ color: '#166534', fontSize: '2rem' }}/>
                                <Typography variant="h5" component="h4" sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, // font-bold
                                    color: '#166534', // Green-800
                                    marginBottom: '0.5rem',
                                }}>
                                    ✨ Pro Tip: Time Saver! ✨
                                </Typography>
                                <Typography variant="body1" sx={{
                                    fontSize: '14px',
                                    color: '#14532d', // Green-900
                                }}>
                                    All Attendees are automatically added when you create Meals, Lodging, or Activities,
                                    helping you populate your event details more efficiently.
                                </Typography>
                            </Card>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Select Attendee */}
                            <Box sx={responsiveBoxItemWithStepSx} >
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-user-plus" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Adding Attendees
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Navigate to your attendee list and find the three dot option.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Select Source */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-expand-vertical" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Select Source
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select how to add: phone contacts (Android), from previous events, or manual entry.
                                        If you are doing a manual entry you can customize the Welcome email!
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Confirm */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-check" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Send the Welcome
                                    </Typography>
                                    <Typography variant="body2" sx={{fontSize: '14px', color: '#4b5563'}}>
                                        Click on the Add button to finish.
                                        <br/><br/>
                                        <Typography component="span" variant="body2"
                                                    sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Extra: </Typography>
                                        If they have an email address they will receive a Welcome email
                                        and a RSVP email automatically.
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>
                    </Box>
                )}

                {tabIndex === 3 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        <Typography variant="h5" component="h5" sx={{
                            fontSize: '16px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Updating Attendee Details
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Select Attendee */}
                            <Box sx={responsiveBoxItemWithStepSx} >
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-user" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Access Attendee's Details
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select the three dots for the attendee whose details need to be revised and click edit.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Select Source */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-pencil" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Edit Details
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Make the necessary adjustments to their details.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Confirm */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-check" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Save Changes
                                    </Typography>
                                    <Typography variant="body2" sx={{fontSize: '14px', color: '#4b5563'}}>
                                        Save your changes to finalize the updated details.
                                        <br/><br/>
                                        <Typography component="span" variant="body2"
                                                    sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Important: </Typography>
                                        Changes made for the Attendee are only applied to the current Event.
                                        If you want to make permanent changes they must be made in the Account Contacts list.
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>
                    </Box>
                )}

                {tabIndex === 4 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        <Typography variant="h5" component="h5" sx={{
                            fontSize: '16px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Removing an Attendee
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Select Attendee */}
                            <Box sx={responsiveBoxItemWithStepSx} >
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-user" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Locate Attendee
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select the three dots for the attendee who needs to be removed.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Select Delete */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-trash" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Initiate Removal
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select the DELETE option
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Confirm */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-check" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Confirm
                                    </Typography>
                                    <Typography variant="body2" sx={{fontSize: '14px', color: '#4b5563'}}>
                                        You will be asked to confirm your intent to remove the Attendee from the
                                        Event.
                                        <br/><br/>
                                        <Typography component="span" variant="body2"
                                                    sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Important: </Typography>
                                        If the Attendee has an email address they will be sent an email notifying them
                                        of the removal from the Event.
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>
                    </Box>
                )}

            </DialogContent>
        </Dialog>
    )
}

export default InfoAttendees;